import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type NameListFieldProps = {
  id: string;
  label: string;
  hint?: string;
  placeholder: string;
  values: string[];
  onChange: (values: string[]) => void;
  emphasizeFirst?: boolean;
};

export function NameListField({
  id,
  label,
  hint,
  placeholder,
  values,
  onChange,
  emphasizeFirst,
}: NameListFieldProps) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const name = draft.trim();
    if (!name || values.includes(name)) {
      setDraft("");
      return;
    }
    onChange([...values, name]);
    setDraft("");
  };

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
      <div className="flex gap-2">
        <Input
          id={id}
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="outline" onClick={add} aria-label={`Add ${label}`}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {values.map((name, index) => (
            <span
              key={`${name}-${index}`}
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm",
                emphasizeFirst && index === 0
                  ? "bg-primary text-white"
                  : "bg-primary/10 text-primary"
              )}
            >
              {emphasizeFirst && index === 0 ? (
                <span className="text-xs">Editor-in-chief</span>
              ) : null}
              {name}
              <button
                type="button"
                className="cursor-pointer rounded-full hover:text-red-500"
                aria-label={`Remove ${name}`}
                onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
